import { getTagListById, saveTagLayers, saveTags } from "../../api/APIHelpers";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";
import { addLookupValueFieldsToTag, lookupNameToId } from "./Helpers";

/**
 * The selected dirty rows that have a saved tag or tag layer to update. Rows without one are new
 * and get deployed to assets instead.
 * @param {object} grid
 * @param {object[]} grid.editedTags Rows as shown in the grid.
 * @param {Set<number>} grid.dirtyTags
 * @param {Set<number>} grid.selectedTags
 * @returns {object[]} In the order the rows became dirty.
 */
export const getTagsToSave = ({ editedTags, dirtyTags, selectedTags }) =>
  [...dirtyTags]
    .filter((index) => selectedTags.has(index))
    .map((index) => editedTags[index])
    .filter((tag) => tag.AssetTagId || tag.TCTagLayerId);

/**
 * Pairs each tag that was sent with its save result and collects the failures.
 * @param {object[]} tags The tags sent, in request order.
 * @param {PromiseSettledResult[]} results Their `Promise.allSettled` results, in the same order.
 * @returns {{ successCount: number,
 *   failures: { tag: object, label: string, reason: string }[] }}
 */
export const summarizeSaveAttempts = (tags, results) => {
  const failures = tags.flatMap((tag, index) =>
    results[index].status === "rejected"
      ? [
          {
            tag,
            label: tag.TagName
              ? `${tag.TagName} (${tag.TagIdentifier})`
              : `TCTagLayerId: ${tag.TCTagLayerId}`,
            reason: extractApiErrorMessage(results[index].reason),
          },
        ]
      : []
  );
  return { successCount: tags.length - failures.length, failures };
};

/**
 * Sends each tag to its saved tag or tag layer and summarizes the results. A tag that fails to
 * save is reported in the summary rather than as a rejection.
 * @param {object[]} tagsToSave
 * @returns {Promise<ReturnType<typeof summarizeSaveAttempts>>}
 */
export const saveTagsAndLayers = async (tagsToSave) => {
  const tagRecords = tagsToSave.filter((tag) => tag.AssetTagId);
  const tagLayers = tagsToSave.filter((tag) => tag.TCTagLayerId);
  const tagResults = await saveTags(tagRecords);
  const tagLayerResults = await saveTagLayers(tagLayers);
  return summarizeSaveAttempts(
    [...tagRecords, ...tagLayers],
    [...tagResults, ...tagLayerResults]
  );
};

/**
 * The rows a save in which at least one tag saved leaves clean: each selected dirty row that did
 * not fail, including selected rows that had no saved record to send. Each maps to the baseline it
 * had when the save started, which still identifies the row if it is edited before the save ends.
 * @param {object} grid The grid when the save started.
 * @param {object[]} grid.editedTags Rows as shown in the grid.
 * @param {object[]} grid.tags Saved baseline for each row, index-aligned with `editedTags`.
 * @param {Set<number>} grid.dirtyTags
 * @param {Set<number>} grid.selectedTags
 * @param {Set<object>} grid.failedTags The rows whose save failed.
 * @returns {Map<object, object | undefined>} Each saved row's baseline, by row.
 */
export const getSavedRows = ({
  editedTags,
  tags,
  dirtyTags,
  selectedTags,
  failedTags,
}) =>
  new Map(
    [...dirtyTags]
      .filter(
        (index) => selectedTags.has(index) && !failedTags.has(editedTags[index])
      )
      .map((index) => [editedTags[index], tags[index]])
  );

/**
 * A new tag list plus copies of the selected rows to save into it, unlinked from the tags and tag
 * layers they came from.
 * @param {object} selection
 * @param {object} selection.tagList The new tag list's fields.
 * @param {object[]} selection.selectedTagRecords The selected rows, in grid order.
 * @param {object[]} selection.lookupValues Lookup value categories from LookupValueContext.
 * @returns {[object, object[]]}
 */
export const buildTagListFromSelection = ({
  tagList,
  selectedTagRecords,
  lookupValues,
}) => [
  { ...tagList },
  selectedTagRecords.map((row) => {
    const tag = { ...row };
    if (tag.TagId) {
      delete tag.TagId;
    }
    if (tag.TCTagLayerId) {
      delete tag.TCTagLayerId;
    }
    if (tag.SourceType && !tag.TagSourceTypeId) {
      tag.TagSourceTypeId = lookupNameToId(
        tag.SourceType,
        "TagSourceType",
        lookupValues
      );
    }
    return tag;
  }),
];

/**
 * Fetches each tag list in turn and returns all their tags as grid rows, each with its list's name
 * and lookup display names. Rejects when any list fails to load.
 * @param {{ TCTagListId: number, Name: string }[]} tagLists
 * @param {object[]} lookupValues Lookup value categories from LookupValueContext.
 * @returns {Promise<object[]>}
 */
export const fetchTagListTags = async (tagLists, lookupValues) => {
  const tags = [];
  for (const tagList of tagLists) {
    const { data } = await getTagListById(tagList.TCTagListId);
    for (const tag of data.Tags) {
      tags.push(
        addLookupValueFieldsToTag(
          { ...tag, TagListName: tagList.Name },
          lookupValues
        )
      );
    }
  }
  return tags;
};
